import { useCallback, useMemo } from 'react';
import type { LucideIcon } from 'lucide-react';
import { ActivityType } from 'app/data/types/activity';
import { FunctionalVertical, UtilityButton, UtilityButtonType } from 'app/data/types/utility-button';
import { useUtilityButtonsQuery } from 'app/data/queries/use-utility-buttons-query';
import { ACTIVITY_TYPE_CONFIG } from 'app/shared/consts/activity';
import { HOST_PLATFORMS } from 'app/shared/consts';
import { useSdkHostInfo } from 'app/shared/contexts/host-info';
import { ENV_URLS, opensOurModule } from 'app/shared/utils/build-activity-deeplink';
import { resolveLucideIcon } from 'app/shared/utils/resolve-lucide-icon';

/**
 * The vertical each activity type belongs to.
 *
 * `event` is absent deliberately: events are not a functional vertical, their icon is fixed and cannot be
 * configured per building, and there is no create flow to gate. Anything unmapped keeps its default icon
 * and stays available.
 */
const VERTICAL_BY_TYPE: Partial<Record<ActivityType, FunctionalVertical>> = {
  resource_booking: FunctionalVertical.RESOURCE_BOOKING,
  visitor_registration: FunctionalVertical.VISITOR_REGISTRATION,
  work_order: FunctionalVertical.SERVICE_REQUEST,
};

/**
 * Buttons for the new work-orders stack carry a `feature_type` and no `vertical`, so they have to be
 * placed by feature. Same map the tenant portal keeps for the same reason.
 */
const VERTICAL_BY_FEATURE_TYPE: Record<string, FunctionalVertical> = {
  SERVICE_REQUESTS_V2: FunctionalVertical.SERVICE_REQUEST,
};

/**
 * The button types that open HqO's OWN experience for a vertical, rather than linking out to someone
 * else's. An absent `type` counts as one: the endpoint always sends it, so a missing field is a gap in
 * the payload, not evidence of a link-out.
 */
const NATIVE_BUTTON_TYPES = new Set<string>([UtilityButtonType.MICROFRONTEND, UtilityButtonType.NATIVE]);

/** The button types that open a url, and so can be followed as-is. NATIVE opens a host screen instead. */
const URL_BUTTON_TYPES = new Set<string>([
  UtilityButtonType.WEBVIEW,
  UtilityButtonType.MICROFRONTEND,
  UtilityButtonType.DEEPLINK,
  UtilityButtonType.NATIVELINK,
]);

/**
 * Where a vertical's create CTA goes (VIS-2189): wherever the building's own button for it goes, on
 * every host that can actually be sent there.
 *
 * - `native`: the button opens HqO's own miniapp for the vertical, so our create screen is that same
 *   app one route deeper — on the button's app instance config, so it lands on the same integration.
 * - `button`: the button links out. The CTA follows its url exactly as tapping the button would,
 *   whether that is the building's own visitor system, another integration, or a deeplink.
 *
 * `undefined` — no target — HIDES the CTA. See `createTargetFor`.
 */
export type CreateTarget =
  | { kind: 'native'; appInstanceUuid?: string }
  | { kind: 'button'; url: string; buttonType: string };

const verticalOf = (button: UtilityButton): FunctionalVertical | undefined => {
  if (button.vertical && button.vertical in FunctionalVertical) {
    return button.vertical as FunctionalVertical;
  }

  return button.feature_type ? VERTICAL_BY_FEATURE_TYPE[button.feature_type] : undefined;
};

export interface BuildingVerticals {
  /** The icon this building chose for a type, or the built-in default. */
  iconFor: (type: ActivityType) => LucideIcon;
  /**
   * Whether the building offers this type to this user at all, and so whether its create affordance
   * is drawn. A type with no vertical (events) is always available; a type whose vertical has no
   * utility button is not. A button this HOST cannot be sent to counts as none — see
   * `createTargetFor` for what "cannot" means and why hiding beats guessing.
   */
  isAvailable: (type: ActivityType) => boolean;
  /**
   * The uuid of the button backing a type's vertical, for hosts that route by button rather than by
   * url. Same button `iconFor` and `isAvailable` read, so a tile's icon and its destination cannot
   * disagree about which instance of the vertical this building means.
   */
  buttonUuidFor: (type: ActivityType) => string | undefined;
  /**
   * Where this type's create CTA should go, read from the same button. `undefined` when the building
   * has no button for the vertical, or one with nothing to follow (VIS-2189).
   */
  createTargetFor: (type: ActivityType) => CreateTarget | undefined;
  /** Buttons are still loading, so availability is not yet known. */
  isLoading: boolean;
}

/**
 * What the building itself says about each vertical: which icon it uses, and whether it has one at all.
 *
 * Read from the utility buttons — the same source the tenant portal's home cards use, so a building's own
 * iconography carries through instead of the feed inventing its own. Availability comes free with it: the
 * endpoint filters by enablement, feature access and per-user permission, so a vertical with no button
 * back is one this user cannot create into here.
 *
 * The endpoint is per USER, not just per building: it drops disabled buttons, buttons denied to the
 * user or their company by feature access, buttons gated on a permission the user lacks, and ones not
 * visible on this device type.
 */
export const useBuildingVerticals = ({ enabled = true }: { enabled?: boolean } = {}): BuildingVerticals => {
  const { utilityButtons, isLoading } = useUtilityButtonsQuery({ enabled });
  const { hostPlatform } = useSdkHostInfo();
  /*
   * Whether the HOST can resolve a utility button from its uuid alone. Only the tenant portal can: it
   * looks the uuid up in its own button list — with its own overrides applied — and routes from that.
   * The native shells read `options.url` and nothing else (verified in both apps: iOS `itemForWebView`
   * never consults `uuid`, and Android parses it but `handleWebViewNavigationCommand` never reads it),
   * so there a button we have no url for is a button we cannot open.
   *
   * Gated on the tenant portal SPECIFICALLY, not on `isDesktopHost` — tenant-web frames this miniapp
   * without that resolution step and would be promised something it cannot deliver.
   */
  const canResolveButtonByUuid = hostPlatform === HOST_PLATFORMS.TENANT_PORTAL;

  // Lowest `order` wins where a building has several buttons for one vertical (meeting spaces, wellness
  // spaces…). Which one that is has not been specified beyond "the first", so it follows the display
  // order the building already chose.
  const buttonByVertical = useMemo(() => {
    const byVertical = new Map<FunctionalVertical, UtilityButton>();

    utilityButtons.forEach((button) => {
      const vertical = verticalOf(button);
      if (!vertical) {
        return;
      }

      const current = byVertical.get(vertical);
      if (!current || button.order < current.order) {
        byVertical.set(vertical, button);
      }
    });

    return byVertical;
  }, [utilityButtons]);

  const iconFor = useCallback(
    (type: ActivityType): LucideIcon => {
      const fallback = ACTIVITY_TYPE_CONFIG[type].icon;
      const vertical = VERTICAL_BY_TYPE[type];

      return vertical ? resolveLucideIcon(buttonByVertical.get(vertical)?.lucide_icon, fallback) : fallback;
    },
    [buttonByVertical],
  );

  /**
   * Where this type's create CTA goes, or `undefined` to HIDE it.
   *
   * ⭐ THE RULE: offer the CTA only where this host can actually be sent to the building's own
   * destination for the vertical. A CTA we cannot resolve is not downgraded to "our flow, near
   * enough" — it is withheld, because landing a tenant in HqO's visitor form when their building
   * books visitors somewhere else is a worse answer than not offering the button at all.
   *
   * The questions, in order, and why THAT order:
   *
   * 1. DOES IT POINT SOMEWHERE THAT ISN'T OURS? Then follow it, with the button's own link type —
   *    precisely what the home-screen button does, since the native shells open `options.url` and
   *    nothing else. Asked FIRST because it is the only question whose wrong answer is actively
   *    misleading: speaking for a button that opens the building's own visitor system lands the
   *    tenant in HqO's form, where the visit they just booked will never appear.
   *
   *    Decided by the URL, not by the integration behind it. An adapter name says which system the
   *    config runs on; it does not say where the BUTTON goes, and those two came apart exactly where
   *    it mattered — Cloudgate is an integration our flow creates into AND a button that links out.
   *    The url is what both destinations are actually built from, so it is the thing that can tell
   *    "our module, one route deeper" from "somebody else's site" (`opensOurModule`).
   *
   * 2. IS IT HQO'S OWN EXPERIENCE? Then our create screen is that same app one route deeper, which
   *    beats the button's own landing page: "Submit a request" opens the form, not the work-orders
   *    home with the form a tap further away. True of a url that resolves to our module, and of a
   *    native-typed button with no url to judge — the latter keeps the behaviour it has today
   *    rather than vanishing on a missing field.
   *
   * 3. CAN THE HOST RESOLVE THE BUTTON ITSELF? Only the tenant portal can: it looks the uuid up in
   *    its OWN button list, overrides included, so it reaches destinations we cannot even see from
   *    here — PENN 1's Cloudgate SAML link-out is manufactured by the portal from an env var the
   *    native apps have no equivalent of. The uuid rides along on every module command, so leaving
   *    the request as it is lets the portal do that. The native shells cannot: they read
   *    `options.url` and never the uuid, so there is nothing for them to resolve.
   *
   * Nothing left → hide. A link-out we have no url for is unreachable from a phone, and an offer we
   * cannot honour is worse than no offer.
   */
  const createTargetFor = useCallback(
    (type: ActivityType): CreateTarget | undefined => {
      const vertical = VERTICAL_BY_TYPE[type];
      const button = vertical ? buttonByVertical.get(vertical) : undefined;
      if (!vertical || !button) {
        return undefined;
      }

      const nativeTarget = { kind: 'native', appInstanceUuid: button.app_instance_uuid || undefined } as const;
      const isOurs = opensOurModule(button.url, type, ENV_URLS);

      // 1. Somewhere that isn't ours, and a url we can follow exactly as the button opens it.
      if (button.url && !isOurs && button.type && URL_BUTTON_TYPES.has(button.type)) {
        return { kind: 'button', url: button.url, buttonType: button.type };
      }

      // 2. Our own module: our create route is the same place, deeper.
      if (isOurs || !button.type || NATIVE_BUTTON_TYPES.has(button.type)) {
        return nativeTarget;
      }

      // 3. A host that resolves the button by uuid. Leave the request as it is and let it.
      if (canResolveButtonByUuid) {
        return nativeTarget;
      }

      // Nothing this host can reach.
      return undefined;
    },
    [buttonByVertical, canResolveButtonByUuid],
  );

  const isAvailable = useCallback(
    (type: ActivityType): boolean => (VERTICAL_BY_TYPE[type] ? createTargetFor(type) !== undefined : true),
    [createTargetFor],
  );

  const buttonUuidFor = useCallback(
    (type: ActivityType): string | undefined => {
      const vertical = VERTICAL_BY_TYPE[type];

      return vertical ? buttonByVertical.get(vertical)?.uuid : undefined;
    },
    [buttonByVertical],
  );

  return { iconFor, isAvailable, buttonUuidFor, createTargetFor, isLoading };
};
